/** The brand profiles a picker's options are limited to, or null for every brand. */
export type BrandScope = readonly string[] | null;

/** An item's live brand profile ids; empty means it applies to every brand. */
export function liveBrandIds(
  assignments: readonly { id: string; isDeleted?: boolean | null }[],
): string[] {
  return assignments.flatMap(({ id, isDeleted }) => (isDeleted ? [] : [id]));
}

/** Whether an item with these brands can be picked within the scope. */
export function isInBrandScope(
  itemBrandIds: readonly string[],
  scope: BrandScope,
): boolean {
  return (
    scope === null ||
    itemBrandIds.length === 0 ||
    itemBrandIds.some((id) => scope.includes(id))
  );
}

/**
 * Narrows the scope to the brands that every picked item shares. Items for
 * every brand leave it as it is, and an item that shares no brand with the
 * scope so far is skipped, since the pickers then leave it out.
 */
export function narrowBrandScope(
  scope: BrandScope,
  itemBrandIds: readonly (readonly string[])[],
): BrandScope {
  return itemBrandIds.reduce<BrandScope>((current, brandIds) => {
    if (brandIds.length === 0) return current;
    if (current === null) return brandIds;
    const shared = current.filter((id) => brandIds.includes(id));
    return shared.length > 0 ? shared : current;
  }, scope);
}

/** The items within the scope; an item without brands applies to every brand. */
export function filterToBrandScope<
  T extends { brandProfileIds?: readonly string[] },
>(items: readonly T[], scope: BrandScope): T[] {
  return items.filter((item) =>
    isInBrandScope(item.brandProfileIds ?? [], scope),
  );
}
